import WiringSetups from './WiringSetups';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { setVlanTrunkApi, type OpenwrtWiring } from '@/api/vendo';

/**
 * Sub-vendo wiring for an OpenWRT (OrangePi) main vendo.
 *
 * A MikroTik tags every VLAN zone on all of its ports, so there is nothing to
 * choose. An OrangePi One has ONE onboard port and ONE USB port, so the
 * operator picks which single link carries the tagged sub-vendo networks.
 * The choices come from the server (per board); which of them the router can
 * actually use comes from the interfaces it reported in its last check-in.
 *
 * Saving does not push anything: the router reads the choice on its next
 * heartbeat, the same way it reads the zones.
 */
const OpenwrtTrunkCard = ({
   deviceId,
   wiring,
   onSaved,
}: {
   deviceId: string;
   wiring: OpenwrtWiring;
   /** Called with the server's fresh wiring block after a save. */
   onSaved: (next: OpenwrtWiring) => void;
}) => {
   const [picked, setPicked] = useState<string>(wiring.trunkIface ?? '');
   const [busy, setBusy] = useState(false);
   useEffect(() => {
      setPicked(wiring.trunkIface ?? '');
   }, [wiring.trunkIface, deviceId]);

   if (!wiring.supported) {
      return (
         <Card data-testid="openwrt-trunk-card">
            <Title>Sub-vendo wiring</Title>
            <Body>
               This router ({wiring.hwBoard || 'unknown board'}) cannot host
               sub-vendos yet. Supported today: <strong>OrangePi One</strong>.
            </Body>
         </Card>
      );
   }

   // A supported board with nothing to pick: a 2-port mini PC (one LAN, one WAN)
   // with no third port yet, or one that has not reported its ports at all.
   if (wiring.trunks.length === 0) {
      return (
         <Card data-testid="openwrt-trunk-card">
            <Title>
               Sub-vendo wiring <BoardTag>{wiring.board}</BoardTag>
            </Title>
            <Body>
               {wiring.netIfaces.length > 0 ? (
                  <>
                     This router reports <strong>{wiring.netIfaces.join(', ')}</strong> —
                     one port for customers and one for the internet, so there is
                     <strong> no spare port</strong> to carry sub-vendo networks. Plug in
                     a <strong>USB-to-LAN adapter</strong> (or use a mini PC with a third
                     port). It shows up here on the router&apos;s next check-in.
                  </>
               ) : (
                  <>The router has not reported its ports yet — it will on its next check-in (about a minute).</>
               )}
            </Body>
         </Card>
      );
   }

   const reported = wiring.netIfaces.length > 0;
   const dirty = picked !== (wiring.trunkIface ?? '');
   // One cable = nothing to choose: the card is a guide to the setup variations,
   // not a form (Ray, 2026-09-23).
   const single = wiring.trunks.length === 1;
   // The pictures show one cable carrying the internet AND the sub-vendo
   // networks: a one-port board, or a bigger one wired that way (MINIPC1,
   // 2026-10-22, onboard port + USB adapter, VLANs on the internet port).
   // Three wordings say it: a one-port mini PC ("The only port"), a bigger mini
   // PC with VLANs on the internet port ("Same port as the internet"), and an
   // OrangePi on its onboard port ("Onboard port only", staging OPI1 2026-10-27).
   const pickedLabel = wiring.trunks.find((t) => t.iface === picked)?.label ?? '';
   const sharesInternetPort =
      wiring.trunks.some((t) => /only port/i.test(t.label)) ||
      /same port as the internet|onboard port only/i.test(pickedLabel);

   const save = async () => {
      if (busy || !dirty) return;
      setBusy(true);
      try {
         const res = await setVlanTrunkApi(deviceId, picked);
         toast.success(res.msg, { autoClose: 5000 });
         onSaved(res.openwrt);
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Failed to save the wiring choice');
      } finally {
         setBusy(false);
      }
   };

   return (
      <Card data-testid="openwrt-trunk-card">
         <Title>
            Sub-vendo wiring <BoardTag>{wiring.board}</BoardTag>
         </Title>
         <Body>
            {single
               ? 'This router has one usable port, so the internet and every sub-vendo network share it. Below are the setup variations that make that work.'
               : 'Pick the cable that carries the sub-vendo networks out of the router.'}
            {reported ? (
               <>
                  {' '}
                  The router reports:{' '}
                  <strong>{wiring.netIfaces.join(', ')}</strong>.
               </>
            ) : (
               <> The router has not reported its ports yet — it will on its next check-in.</>
            )}
         </Body>
         <Options role="radiogroup" aria-label="Sub-vendo trunk">
            {wiring.trunks.map((t) => {
               const missing = t.available === false;
               const selected = picked === t.iface;
               return (
                  <Option
                     key={t.iface}
                     type="button"
                     role="radio"
                     aria-checked={selected}
                     aria-label={t.label}
                     $selected={selected}
                     $missing={missing}
                     disabled={busy || missing}
                     title={missing ? 'The router does not report this link' : undefined}
                     onClick={() => setPicked(t.iface)}
                  >
                     <OptionHead>
                        <Radio $on={selected} />
                        <OptionLabel>{t.label}</OptionLabel>
                        <Iface>{t.iface}</Iface>
                        {t.iface === wiring.trunkIface && !dirty && <InUse>in use</InUse>}
                        {missing && <Missing>not plugged in</Missing>}
                     </OptionHead>
                     <OptionHint>{t.hint}</OptionHint>
                  </Option>
               );
            })}
         </Options>
         {sharesInternetPort && (
            <Setups>
               <SetupsTitle>Setup variations</SetupsTitle>
               <WiringSetups compact />
            </Setups>
         )}
         {!single && (
         <Actions>
            <SaveBtn type="button" disabled={busy || !dirty} onClick={save}>
               {busy ? 'Saving…' : 'Save wiring'}
            </SaveBtn>
            <Note>
               Applies on the router&apos;s next check-in (about a minute). No
               reboot.
            </Note>
         </Actions>
         )}
      </Card>
   );
};

export default OpenwrtTrunkCard;

const Card = styled.div`
   background: var(--app-surface, ${colors.WHITE});
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 14px 16px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const Title = styled.h3`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   display: flex;
   align-items: center;
   gap: 8px;
`;

const BoardTag = styled.span`
   font-size: 10px;
   font-weight: 700;
   padding: 2px 8px;
   border-radius: 999px;
   background: ${colors.PRIMARY}14;
   color: ${colors.PRIMARY};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const Body = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.5;
   strong { color: ${colors.MAIN_DARK}; }
`;

const Options = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 10px;
   @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Option = styled.button<{ $selected: boolean; $missing: boolean }>`
   text-align: left;
   font-family: inherit;
   cursor: ${({ $missing }) => ($missing ? 'not-allowed' : 'pointer')};
   opacity: ${({ $missing }) => ($missing ? 0.55 : 1)};
   background: ${({ $selected }) => ($selected ? `${colors.PRIMARY}08` : 'transparent')};
   border: 2px solid ${({ $selected }) => ($selected ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   padding: 10px 12px;
   display: flex;
   flex-direction: column;
   gap: 6px;
   transition: border-color 0.15s;
   &:hover:not(:disabled) { border-color: ${colors.PRIMARY}; }
`;

const OptionHead = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const Radio = styled.span<{ $on: boolean }>`
   width: 14px;
   height: 14px;
   border-radius: 50%;
   border: 2px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY10)};
   background: ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
   box-shadow: inset 0 0 0 2px var(--app-surface, ${colors.WHITE});
   flex: none;
`;

const OptionLabel = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const Iface = styled.span`
   font-family: monospace;
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const InUse = styled.span`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.SUCCESS ?? '#1a9c5b'};
   text-transform: uppercase;
`;

const Missing = styled.span`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
`;

const OptionHint = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.45;
`;

const Actions = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   flex-wrap: wrap;
`;

const SaveBtn = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 7px 14px;
   border-radius: 8px;
   border: 1px solid ${colors.PRIMARY};
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;

const Note = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const Setups = styled.div`
   margin-top: 12px;
   padding-top: 10px;
   border-top: 1px dashed #e5e7eb;
`;
const SetupsTitle = styled.div`
   font-weight: 600;
   font-size: 13px;
   margin-bottom: 6px;
   color: #111827;
`;
